// src/Pages/ForgotPasswordPage.tsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { requestPasswordReset } from '@/lib/passwordReset';
import { ArrowLeft, Mail, CheckCircle } from 'lucide-react';

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [debugLink, setDebugLink] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const result = await requestPasswordReset(email);
      
      if (result.success) {
        setSuccess(true);
        if (result.debugResetLink) {
          setDebugLink(result.debugResetLink);
        } else {
          setDebugLink(null);
        }
      } else {
        setError(result.error || 'Failed to send reset email');
      }
    } catch (err) {
      console.error('Password reset request failed:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#040109] via-[#10041d] to-[#1a0930] flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-800 rounded-2xl p-8 text-center space-y-6">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-8 h-8 text-green-400" />
            </div>
            
            <div className="space-y-2">
              <h2 className="text-2xl font-bold text-white">Check Your Email</h2>
              <p className="text-slate-300">
                If an account exists for <span className="text-blue-400 font-semibold">{email}</span>, you will receive a password reset link shortly.
              </p>
              <p className="text-sm text-slate-400 pt-4 space-y-1">
                <span className="block">The reset link will expire in 15 minutes.</span>
                <span className="block">If you don’t see it, check Spam/All Mail or search for “WHIPT” or “Supabase Auth”.</span>
              </p>
            </div>

            {debugLink && (
              <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl text-left text-slate-200 text-sm space-y-2">
                <div className="font-semibold text-blue-200">Dev mode reset link (copy manually)</div>
                <code className="block break-all text-xs bg-slate-900/60 p-2 rounded">{debugLink}</code>
                <p className="text-xs text-slate-400">Visible because dev mode is enabled on this device.</p>
              </div>
            )}

            <div className="space-y-3 pt-4">
              <button
                onClick={() => navigate('/organizer/sign-in')}
                className="w-full px-6 py-3 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white font-semibold transition-all"
              >
                Return to Sign In
              </button>
              
              <button
                onClick={() => {
                  setSuccess(false);
                  setEmail('');
                }}
                className="w-full px-6 py-3 rounded-xl border border-slate-700 hover:border-slate-600 text-slate-300 font-semibold transition-all"
              >
                Send Another Email
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#040109] via-[#10041d] to-[#1a0930] flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <button
          onClick={() => navigate('/organizer/sign-in')}
          className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Sign In</span>
        </button>

        <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-800 rounded-2xl p-8">
          <div className="space-y-2 mb-6">
            <h2 className="text-2xl font-bold text-white">Forgot Password?</h2>
            <p className="text-slate-400">
              Enter your email address and we'll send you a link to reset your password.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm">
                {error}
              </div>
            )}

            <label className="block space-y-2">
              <span className="text-sm font-medium text-slate-300">Email Address</span>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 bg-slate-800/50 border border-slate-700 rounded-xl text-white placeholder:text-slate-500 focus:border-blue-500 focus:outline-none transition-colors"
                  placeholder="your@email.com"
                  required
                  disabled={isLoading}
                />
              </div>
            </label>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full px-6 py-3 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white font-semibold transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Sending...</span>
                </>
              ) : (
                <>
                  <Mail className="w-5 h-5" />
                  <span>Send Reset Link</span>
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-slate-400">
              Remember your password?{' '}
              <button
                onClick={() => navigate('/organizer/sign-in')}
                className="text-blue-400 hover:text-blue-300 font-semibold transition-colors"
              >
                Sign In
              </button>
            </p>
          </div>
        </div>

        <div className="mt-6 text-center text-xs text-slate-500">
          <p>For security reasons, we don't reveal whether an email exists in our system.</p>
        </div>
      </div>
    </div>
  );
}
